import * as React from "react";
import { inject, observer } from "mobx-react";
import { Locale } from 'date-fns';
import { Text } from "telia-front-react/text";
import { Grid, GridColumn } from "telia-front-react/grid";
import { Button } from "telia-front-react/button";
import { Heading } from "telia-front-react/heading";
import { Icon, IIconProps } from "telia-front-react/icon";
import { Modal, ModalContent, ModalFooter } from "telia-front-react/modal";
import { Separator } from "telia-front-react/separator";
import { Table } from "telia-front-react/table";
import config from "@config";
import { Disturbance, DisturbanceLocation, DisturbanceType } from "@config/DisturbanceConfig";
import DisturbanceLocationModalStore from "@stores/DisturbanceLocationModalStore";
import LanguageStore from "@stores/LanguageStore";
import DisturbanceModalStore from "@stores/DisturbanceModalStore";
import SsoStore from "@stores/SsoStore";
import { formatDate } from "@util/DateUtil";

interface DisturbanceLocationModalProps {
    disturbanceLocationModalStore?: DisturbanceLocationModalStore;
    disturbanceModalStore?: DisturbanceModalStore;
    languageStore?: LanguageStore;
    ssoStore?: SsoStore;
}

interface DisturbanceFailureLabelProps {
    label: string;
    children: React.ReactChild | React.ReactChild[];
}

/**
 * Render DisturbanceLocationModal
 * @param props Required properties for DisturbanceLocationModal
 * @constructor
 */
const DisturbanceLocationModal = (props: DisturbanceLocationModalProps) => {
    const {
        disturbanceLocationModalStore: {
            disturbance, customerId, modalIsOpen,
            affectedLocations, location, type
        },
        disturbanceLocationModalStore,
        disturbanceModalStore,
        ssoStore,
        languageStore: { currentLocale }
    } = props;

    return disturbance && customerId ? (
        <Modal
            size="10-col"
            hasContentComponent={true}
            aria-labelledby="affected-modal-heading"
            aria-describedby="affected-description"
            isOpen={modalIsOpen}
            onClose={() => {
                disturbanceLocationModalStore.closeModal();
            }}
        >
            <ModalContent>
                <Text>
                    <Heading className="h3" id="affected-modal-heading">
                        Katkestusest mõjutatud teenused ({affectedLocations.length})
                    </Heading>
                    <Separator spacing="xsmall" color="invisible" />
                    <Grid noWrap={['sm']}>
                        <GridColumn width={['xs-12', 'max-sm']}>
                            <p id="affected-description">
                                Siin näed, kui kaua kestab katkestus aadressil <b>{location.name}</b> ning milliseid teenuseid see puudutab.
                            </p>
                        </GridColumn>
                        <GridColumn width={['xs-12', 'min-sm']}>
                            <Button
                                text="Kontrollin oma teenuseid" 
                                iconRight="#arrow-right"
                                size="small"
                                url={config.controlUrl}
                                target="_blank"
                            />
                        </GridColumn>
                    </Grid>
                    {
                        getAffectedServices(
                            ssoStore.isDealer,
                            affectedLocations,
                            disturbance,
                            type,
                            disturbanceModalStore,
                            currentLocale
                        )
                    }
                </Text>
            </ModalContent>
            <ModalFooter>
                <Grid align={['end-xs']}>
                    <GridColumn width={['xs']}>
                        <Button
                            text="Sulgen"
                            onClick={(event) => {
                                event.preventDefault();
                                disturbanceLocationModalStore.closeModal();
                            }}
                        />
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    ) : ( 
        <></>
    );
};

const getAffectedServices = (
    isDealer: boolean,
    locations: DisturbanceLocation[],
    disturbance: Disturbance,
    type: DisturbanceType,
    disturbanceModalStore: DisturbanceModalStore,
    currentLocale: Locale
) : JSX.Element => {
    const iconProps: IIconProps = {
        modifiers: type === DisturbanceType.IncidentDisturbanceInfo ? '#alert' : '#calendar',
        size: 'sm',
    };
    return (
        <Text>
            <Grid noWrap={["xs"]}>
                <GridColumn width={['xs-12', 'min-sm']} noGutter={true}>
                    <Icon {...iconProps} />
                </GridColumn>
                <GridColumn width={['xs-12', 'max-sm']}>
                    <DisturbanceFailureLabel label="Katkestuse liik:">
                        {type === DisturbanceType.ChangeDisturbanceInfo ? 'Plaaniline hooldustöö' : 'Üldrike'}
                        {' '}
                        {<span>{"("}ID: {isDealer ? (
                            <Button
                                id={disturbance.id}
                                text={disturbance.id}
                                link={true}
                                type="employee"
                                onClick={(event) => {
                                    event.preventDefault();
                                    disturbanceModalStore.openModal(disturbance, type);
                                }}
                            />
                        ) : (<>{disturbance.id}</>) }{")"}</span>}
                    </DisturbanceFailureLabel>
                    <DisturbanceFailureLabel label="Algus:">
                        {formatDisturbanceDate(disturbance.start, currentLocale)}
                    </DisturbanceFailureLabel>
                    { (disturbance.plannedEnd || disturbance.end) ? (
                        disturbance.end ? (
                            <DisturbanceFailureLabel label="Lõpp:">
                                {formatDisturbanceDate(disturbance.end, currentLocale)}
                            </DisturbanceFailureLabel>
                        ) : (
                            <DisturbanceFailureLabel label="Eeldatav lõpp:">
                                {formatDisturbanceDate(disturbance.plannedEnd, currentLocale)}
                            </DisturbanceFailureLabel>
                        )
                        ) : <></>}
                </GridColumn>
            </Grid>
            <Separator spacing="xsmall" color="invisible"/>
            {getServicesTable(
                locations
            )}
        </Text>

    );
};

const getServicesTable = (
    locations: DisturbanceLocation[]
): JSX.Element => {
    return (
        <Table
            stickyHeader="level-1"
            maxHeight={300}
            className="text__margin-half"
        >
            <thead>
                <tr className="table__row table__row--header">
                    <th className="table__cell" scope="col">Teenus</th>
                    <th className="table__cell" scope="col">Sidenumber</th>
                </tr>
            </thead>
            <tbody>
                {locations.map((location, locationKey) => getServiceRow(
                    location.serviceName, location.serviceId, locationKey
                ))}
            </tbody>
        </Table>
    );
};

const getServiceRow = (
    serviceName: string,
    serviceId: string,
    serviceKey: number
): JSX.Element => {
    const rowClass = 'table__row text-small'
    const cellClass = 'table__cell table__cell--valign-top'
        
    return (
        <tr key={serviceKey} className={rowClass}>
            <td className={cellClass}>
                {serviceName}
            </td>
            <td className={cellClass}>
                {serviceId}
            </td>
        </tr>
    );
};

const formatDisturbanceDate = (date: string, currentLocale: Locale) => {
    return `Kell ${formatDate(date, 'HH:mm, dd. MMMM yyyy', currentLocale)}`;
};

const DisturbanceFailureLabel = (props: DisturbanceFailureLabelProps): JSX.Element => {
    const { label, children } = props;
    return (
        <p>
            <Grid align={['middle-xs']} verticalGutter="none" element="span">
                <GridColumn width={['xs', 'sm-4', 'md-3']}>
                    {label}
                </GridColumn>
                <GridColumn width={['xs']} className="text-bold">
                    {children}
                </GridColumn>
            </Grid>
        </p>
    );
};

export default inject(
    'disturbanceLocationModalStore',
    'disturbanceModalStore',
    'languageStore',
    'ssoStore'
)(observer(DisturbanceLocationModal));
